import React, { useRef, useState, useEffect } from 'react';
import { Box, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';

const DigitInput = styled(TextField)({
  width: '48px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    '& input': {
      textAlign: 'center',
      fontSize: '20px',
      fontWeight: 600,
      padding: '12px 8px',
      fontFamily: "'General Sans', sans-serif",
    }
  }
});

export default function OTPInput({ value, onChange, disabled = false, length = 6 }) {
  const [digits, setDigits] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (value) {
      const valueDigits = value.split('').slice(0, length);
      const paddedDigits = [...valueDigits, ...Array(length - valueDigits.length).fill('')];
      setDigits(paddedDigits);
    } else {
      setDigits(Array(length).fill(''));
    }
  }, [value, length]);

  const handleChange = (index, e) => {
    const val = e.target.value;
    
    if (val.length > 1) {
      const pastedDigits = val.replace(/\D/g, '').split('').slice(0, length);
      const newDigits = [...digits];
      pastedDigits.forEach((digit, i) => {
        if (index + i < length) {
          newDigits[index + i] = digit;
        }
      });
      setDigits(newDigits);
      onChange(newDigits.join(''));
      
      const nextIndex = Math.min(index + pastedDigits.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    if (!/^\d*$/.test(val)) return;

    const newDigits = [...digits];
    newDigits[index] = val;
    setDigits(newDigits);
    onChange(newDigits.join(''));

    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleFocus = (e) => {
    e.target.select();
  };

  return (
    <Box sx={{ display: 'flex', gap: '8px' }}>
      {digits.map((digit, index) => (
        <DigitInput
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={digit}
          onChange={(e) => handleChange(index, e)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onFocus={handleFocus}
          disabled={disabled}
          inputProps={{
            maxLength: length,
            inputMode: 'numeric',
            pattern: '[0-9]*'
          }}
          size="small"
        />
      ))}
    </Box>
  );
}
